import type { Graphics } from 'pixi.js';
import { clamp } from '../../shared/scalar';
import { TAU } from '../paint';
import { angleSpring, angleTo, spring } from '../player-look';
import { UNIT } from './celeste-paint';

export const TAILS = [
  { root: [-4, -7], spread: 0.5, length: 22, swing: 0.276, phase: 0 },
  { root: [-4, 7], spread: -0.5, length: 22, swing: 0.345, phase: 2.1 },
  { root: [-4, 0], spread: 0, length: 25, swing: 0.224, phase: 4.2 },
] as const;
const RIBBON = { steps: 14, root: 5.2, tip: 2.6, point: 0.82, edge: 1.6 } as const;
export const CAPE_STARS = { steps: [5, 10], sparkle: 0.32 } as const;
const NIGHT = 0x141a4a;
const OUTLINE = 2;

const MOTION = {
  normal: {
    damping: 0.5,
    softer: 1,
    stretch: 0.85,
    narrow: 0.3,
    sway: 0.09,
    swayBeats: 4,
    ripple: 0.25,
    rippleRush: 2.2,
    rippleBeats: 2,
  },
  calm: {
    damping: 0.9,
    softer: 0.7,
    stretch: 0.35,
    narrow: 0.15,
    sway: 0.05,
    swayBeats: 8,
    ripple: 0,
    rippleRush: 0,
    rippleBeats: 2,
  },
} as const;
const SPRING = { rootStiffer: 1.7, lengthStiffer: 1.4, most: 1.7 } as const;
const TRAIL = { lean: 1.25, walk: 0.25, smooth: 0.2 } as const;

export interface CapeMotion {
  x: number;
  y: number;
  facing: number;
  size: number;
  heading: number;
  moving: number;
  speed: number;
  beats: number;
  dt: number;
  calm: boolean;
}

const SPRING_VALUES_PER_TAIL = 6;
const POINTS = RIBBON.steps + 1;

export class Cape {
  private readonly springs = new Float64Array(TAILS.length * SPRING_VALUES_PER_TAIL);
  private rush = 0;
  private fresh = true;
  readonly middles = TAILS.map(() => new Array<number>(POINTS * 2).fill(0));
  readonly outlines = TAILS.map(() => new Array<number>(POINTS * 4).fill(0));

  reset(): void {
    this.fresh = true;
    this.rush = 0;
  }

  place(motion: CapeMotion): void {
    const { facing, heading, moving, speed, beats, dt, calm } = motion;
    const style = calm ? MOTION.calm : MOTION.normal;
    const rush = Math.min(1, TRAIL.walk * speed * speed);
    this.rush = this.fresh
      ? rush
      : this.rush + (rush - this.rush) * (1 - Math.exp(-dt * TRAIL.smooth));
    const behind = facing + Math.PI;
    const lean = clamp(angleTo(behind, heading + Math.PI) * moving, -TRAIL.lean, TRAIL.lean);
    const hang = behind + lean;
    const { springs } = this;
    TAILS.forEach((tail, index) => {
      const at = index * SPRING_VALUES_PER_TAIL;
      const sway = style.sway * Math.sin((TAU * beats) / style.swayBeats + tail.phase);
      const aim = hang + tail.spread * (1 - style.narrow * rush);
      const rootAim = aim + sway / 2;
      const tipAim = aim + sway;
      const length = tail.length * (1 + style.stretch * rush);
      if (this.fresh) {
        springs.set([rootAim, 0, tipAim, 0, tail.length, 0], at);
      }
      const swing = tail.swing * style.softer;
      swingTo(springs, at, rootAim, swing * SPRING.rootStiffer, style.damping, dt);
      swingTo(springs, at + 2, tipAim, swing, style.damping, dt);
      const stretch = swing * SPRING.lengthStiffer;
      spring(springs, at + 4, length, stretch * stretch, 2 * style.damping * stretch, dt);
      this.shape(index, motion, style.ripple + style.rippleRush * this.rush, style.rippleBeats);
    });
    this.fresh = false;
  }

  draw(
    cape: Graphics,
    outline: Graphics | null,
    color: number,
    outlineColor: number,
    size: number,
  ): void {
    const edge = RIBBON.edge * UNIT * size;
    cape.clear();
    outline?.clear();
    for (const points of this.outlines) {
      outline
        ?.poly(points)
        .fill(outlineColor)
        .stroke({ width: edge + OUTLINE, color: outlineColor, join: 'round' });
      cape.poly(points).fill(NIGHT).stroke({ width: edge, color, join: 'round' });
    }
  }

  private shape(index: number, motion: CapeMotion, ripple: number, rippleBeats: number): void {
    const tail = TAILS[index];
    const middle = this.middles[index];
    const outline = this.outlines[index];
    if (tail === undefined || middle === undefined || outline === undefined) {
      return;
    }
    const { x, y, facing, size, beats } = motion;
    const at = index * SPRING_VALUES_PER_TAIL;
    const unit = UNIT * size;
    const cos = Math.cos(facing);
    const sin = Math.sin(facing);
    const [along, across] = tail.root;
    const x0 = x + (along * cos - across * sin) * unit;
    const y0 = y + (along * sin + across * cos) * unit;
    const half = ((this.springs[at + 4] ?? tail.length) / 2) * unit;
    const rootAngle = this.springs[at] ?? 0;
    const tipAngle = this.springs[at + 2] ?? 0;
    const cx = x0 + Math.cos(rootAngle) * half;
    const cy = y0 + Math.sin(rootAngle) * half;
    const x1 = cx + Math.cos(tipAngle) * half;
    const y1 = cy + Math.sin(tipAngle) * half;
    const last = POINTS * 4 - 2;
    for (let step = 0; step < POINTS; step += 1) {
      const u = step / RIBBON.steps;
      const a = (1 - u) * (1 - u);
      const b = 2 * u * (1 - u);
      const c = u * u;
      const dx = 2 * (1 - u) * (cx - x0) + 2 * u * (x1 - cx);
      const dy = 2 * (1 - u) * (cy - y0) + 2 * u * (y1 - cy);
      const d = Math.hypot(dx, dy) || 1;
      const nx = -dy / d;
      const ny = dx / d;
      const wave = ripple * u * Math.sin(TAU * (u * 0.8 - beats / rippleBeats) + tail.phase) * unit;
      const px = a * x0 + b * cx + c * x1 + nx * wave;
      const py = a * y0 + b * cy + c * y1 + ny * wave;
      const taper = u < RIBBON.point ? 1 : 1 - (u - RIBBON.point) / (1 - RIBBON.point);
      const width = (RIBBON.root + (RIBBON.tip - RIBBON.root) * u) * taper * unit;
      middle[step * 2] = px;
      middle[step * 2 + 1] = py;
      outline[step * 2] = px + nx * width;
      outline[step * 2 + 1] = py + ny * width;
      outline[last - step * 2] = px - nx * width;
      outline[last - step * 2 + 1] = py - ny * width;
    }
  }
}

function swingTo(
  springs: Float64Array,
  at: number,
  target: number,
  rate: number,
  damping: number,
  dt: number,
): void {
  angleSpring(springs, at, target, rate * rate, 2 * damping * rate, dt);
  const off = angleTo(target, springs[at] ?? target);
  if (Math.abs(off) > SPRING.most) {
    springs[at] = target + Math.sign(off) * SPRING.most;
  }
}
